import React, { useState } from "react";

import { programAllows, useGetFarmDetails, useGetFarmReportingYear, useGetFarms, useGetFieldsByFarmId, useTranslation } from "utils";

import { Drawer, Skeleton } from "components";

import { FieldUpload } from "pages/app/fields/components/FieldUpload";
import { FarmMapPanel } from "pages/app/home/farm-map/FarmMapPanel";
import { HomeGreeting } from "pages/app/home/hero/HomeGreeting";
import { HomeHeadline } from "pages/app/home/hero/HomeHeadline";
import { cardsForSegment, getHeroView, getHomeSegment } from "pages/app/home/hero/homeSegment";
import { HomeStatusCards } from "pages/app/home/status-cards/HomeStatusCards";
import { useHomeFieldStats } from "pages/app/home/status-cards/useHomeFieldStats";
import { useSoilPlus3Allotment } from "pages/app/home/status-cards/useSoilPlus3Allotment";

const heightVariantForCards = (cardCount: number): "cards" | "two-cards" | "full" => (cardCount >= 3 ? "cards" : cardCount > 0 ? "two-cards" : "full");

/** The top of the farmer home page behind the new-homepage-hero flag: greeting band, farm map and the segment's status cards. */
export const HomeHero = () => {
  const { t } = useTranslation(["home", "allFieldView"]);
  const { currentFarm, isLoading: farmsLoading } = useGetFarms();
  const farmId = currentFarm?.id ?? "";
  const { data: farmDetails, isLoading: farmDetailsLoading } = useGetFarmDetails({ farmId });
  const {
    data: { activeFarmReportingYear },
    isLoading: reportingYearLoading,
  } = useGetFarmReportingYear(currentFarm?.id ?? null);
  // a failed refetch keeps the cached fields: only a failed first load leaves them unknown
  const { data: fields = [], isLoading: fieldsLoading, isLoadingError: fieldsUnavailable } = useGetFieldsByFarmId({ farmId });
  // the map bar's crops and the footprinting forms are per calendar year; field management and the payout follow the reporting year
  const currentYear = new Date().getFullYear();
  const reportingYear = activeFarmReportingYear ?? currentYear;
  const { usesAllotment, loading: allotmentLoading, unavailable: allotmentUnavailable } = useSoilPlus3Allotment(currentFarm?.id, reportingYear);
  const {
    stats: fieldStats,
    fieldStatusMap,
    loading: fieldStatsLoading,
    unavailable: fieldStatsUnavailable,
  } = useHomeFieldStats(currentFarm, fields, reportingYear, currentYear);
  const [showFieldUploadDrawer, setShowFieldUploadDrawer] = useState(false);

  if (farmsLoading) {
    return (
      <div className="home-hero" data-testid="home-hero-loading">
        <Skeleton type="block" height="26rem" />
      </div>
    );
  }
  // the farms failed to load or the user has no farm: the rest of the page still shows
  if (!currentFarm) return null;

  // programs without field management never ask for fields, so their cards are not in a "no fields" state
  const hasFieldManagement = programAllows(currentFarm.carbonProgram, "fields");
  // a failed field request leaves the fields unknown: never the "add your fields" state, the cards say so instead
  const hasFields = !hasFieldManagement || fieldsUnavailable || fields.length > 0;
  const cardsLoading = fieldsLoading || reportingYearLoading || allotmentLoading;
  // the no-program fallback needs the fields and the farm's flags, so a farm never shows it while either loads or failed
  const programSegment = getHomeSegment(currentFarm.carbonProgram);
  const { segment, cards } =
    cardsLoading || fieldsUnavailable || allotmentUnavailable
      ? { segment: programSegment, cards: cardsForSegment(programSegment, currentFarm.carbonProgram, currentYear) }
      : getHeroView(programSegment, currentFarm.carbonProgram, currentYear, { usesAllotment, hasFields });

  return (
    <section className="home-hero" data-testid="home-hero">
      <div className="home-hero-band">
        <HomeGreeting />
        <HomeHeadline segment={segment} farmName={currentFarm.name} reportingYear={reportingYear} />
      </div>
      <div className="home-hero-row">
        <FarmMapPanel
          key={currentFarm.id}
          farm={currentFarm}
          city={farmDetails?.addressCity ?? null}
          cityLoading={farmDetailsLoading}
          country={farmDetails?.country ?? null}
          cropYear={currentYear}
          fields={fields}
          fieldsLoading={fieldsLoading}
          fieldStats={fieldStats}
          fieldStatsLoading={fieldStatsLoading}
          fieldsUnavailable={fieldsUnavailable}
          fieldStatsUnavailable={fieldStatsUnavailable}
          fieldStatusMap={fieldStatusMap}
          heightVariant={heightVariantForCards(cards.length)}
          hasFieldManagement={hasFieldManagement}
          onAddFields={() => setShowFieldUploadDrawer(true)}
        />
        <HomeStatusCards
          farm={currentFarm}
          segment={segment}
          cards={cards}
          reportingYear={reportingYear}
          currentYear={currentYear}
          hasFields={hasFields}
          fieldStats={fieldStats}
          fieldStatsLoading={fieldStatsLoading}
          fieldsUnavailable={fieldsUnavailable}
          fieldStatsUnavailable={fieldStatsUnavailable}
          allotmentUnavailable={allotmentUnavailable}
          loading={cardsLoading}
        />
      </div>
      <Drawer
        dataTestId="home-hero-add-fields-drawer"
        title={t("allFieldView:titleUpdateDrawer")}
        visible={showFieldUploadDrawer}
        onClose={() => setShowFieldUploadDrawer(false)}
      >
        <FieldUpload onFinish={() => setShowFieldUploadDrawer(false)} />
      </Drawer>
    </section>
  );
};
